import { generateClient } from 'aws-amplify/data';
import { uploadData } from 'aws-amplify/storage';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { FormData } from './formValidation';
import type { Schema } from '../../amplify/data/resource';
import { ApplicationDetail, createApplicationData, serializeApplicationDetail, deserializeApplicationDetail } from '../utils/applicationUtils';

// Initialize client lazily to avoid Amplify configuration warnings
let client: ReturnType<typeof generateClient<Schema>> | null = null;

const getClient = () => {
  if (!client) {
    client = generateClient<Schema>();
  }
  return client;
};

export interface ApplicationSubmission {
  id?: string;
  applicationId?: string;
  applicationYear?: string;
  applicationDetail: ApplicationDetail;
  applicationStatus: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

// Check if Application model is available
const isApplicationModelAvailable = () => {
  try {
    //// console.log('🔍 Checking Application model availability...');
    //// console.log('client.models:', client.models);
    //// console.log('client.models.Application:', client.models?.Application);
    const isAvailable = getClient().models && getClient().models.Application;
    //// console.log('✅ Application model available:', isAvailable);
    return isAvailable;
  } catch (error) {
    console.error('❌ Error checking Application model:', error);
    return false;
  }
};

export class ApplicationService {
  async submitApplication(formData: FormData): Promise<{ success: boolean; message: string; applicationId?: string }> {
    try {
      // console.log('Starting application submission...');

      // Check if Application model is available
      if (!isApplicationModelAvailable()) {
        console.warn('⚠️ Application model not available - using local storage fallback');
        return {
          success: false,
          message: 'Application model not available'
        };
      }

      // Upload files first
      const fileUrls = await this.uploadFiles(formData);
      // console.log('Files uploaded:', fileUrls);

      // Prepare application detail data
      const applicationDetail: ApplicationDetail = {
        // Entry Information
        companyNameEng: formData.company_name_eng,
        companyNameChi: formData.company_name_chi || '',
        entryTitleEng: formData.entry_title_eng,
        entryTitleChi: formData.entry_title_chi || '',
        entryDescription: formData.entry_info,
        companyDescription: formData.company_info,
        businessRegNo: formData.company_registration_no,
        incorporationNo: formData.company_incorporation_no,
        incorporationDate: formData.company_incorporation_date,
        companyAddress: formData.company_address,
        companyCEO: formData.company_ceo_name || '',
        companyBusiness: formData.company_business,
        companyURL: formData.company_url,
        isStartup: formData.startup,
        employeeCount: formData.company_employee_no || '',
        incubationProgram: formData.entry_incubation,
        incubationDescription: formData.entry_incubation_description || '',

        // File URLs
        logoAIUrl: fileUrls.logoAI || '',
        logoJPEGUrl: fileUrls.logoJPEG || '',
        brandGuidelineUrl: fileUrls.brandGuideline || '',
        entryPhotoUrl: fileUrls.entry_photo || '',
        supportingDocumentUrl: fileUrls.judging_doc_material || '',

        // Contact Details
        primaryContactName: formData.contact_name_1,
        primaryContactTitle: formData.contact_title_1,
        primaryContactPhone: formData.contact_phone_1,
        primaryContactEmail: formData.contact_email_1,
        secondaryContactName: formData.contact_name_2,
        secondaryContactTitle: formData.contact_title_2,
        secondaryContactPhone: formData.contact_phone_2,
        secondaryContactEmail: formData.contact_email_2,

        // Award Category
        awardCategory: formData.award_category,

        // Judging Materials
        innovationCreativity: formData.judging_specialfeature_1,
        functionalityErgonomics: formData.judging_specialfeature_2,
        quality: formData.judging_specialfeature_3,
        benefitsImpact: formData.judging_specialfeature_4,
        marketPotential: formData.judging_specialfeature_5,
        ecologicalCompatibility: formData.judging_specialfeature_6,
        videoLink: formData.judging_video_material_1 || '',

        // Declaration
        submissionUsage: formData.about_submission_usage,
        applicationDeclaration: formData.about_application,
        patentStatus: formData.entry_patent,
        patentNumber: formData.entry_patent_number || '',
        patentTitle: formData.entry_patent_title || '',
        patentProprietor: formData.entry_patent_proprietor || '',
        patentCountry: formData.entry_patent_country || '',
        patentDate: formData.entry_patent_date || '',
        patentStatusType: formData.entry_patent_status || '',
        intellectualProperty: formData.entry_dispute,
        disputeDescription: formData.entry_dispute_description || '',
        eventAdminCost: formData.event_cost,
        verifyCode: formData.checknum,
      };

      // Generate application ID directly (no Lambda function needed)
      // console.log('Generating application ID...');
      const timestamp = Date.now();
      const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      const applicationId = `2025${timestamp.toString().slice(-6)}${randomSuffix}`;
      // console.log('Generated application ID:', applicationId);

      // Create application data with new structure (applicationId and applicationYear are separate fields)
      const applicationData = createApplicationData(applicationDetail);
      const serializedDetail = serializeApplicationDetail(applicationDetail);

      // console.log('Saving application data:', applicationData);

      // Get current user for updatedBy field (for public submissions, use 'system')
      const currentUsername = 'system'; // Public form submissions always use 'system'

      // Save to DynamoDB using Amplify Data API
      const result = await getClient().models.Application.create({
        applicationId,
        applicationYear: '2025',
        applicationDetail: serializedDetail,
        applicationStatus: applicationData.applicationStatus,
        status: applicationData.status,
        createdAt: applicationData.createdAt,
        updatedAt: applicationData.updatedAt,
        updatedBy: currentUsername,
      }, {
        authMode: 'apiKey' // Use API key for public form submissions
      });
      // console.log('Application saved successfully:', result);

      if (!result.data) {
        throw new Error('Failed to save application - no data returned');
      }

      // Send confirmation emails to both contacts
      await this.sendConfirmationEmail(
        applicationId,
        formData.company_name_eng,
        formData.company_name_chi,
        formData.entry_title_eng,
        formData.entry_title_chi,
        formData.contact_name_1,
        formData.contact_email_1,
        formData.contact_name_2,
        formData.contact_email_2,
      );

      return {
        success: true,
        message: 'Application submitted successfully! You will receive a confirmation email shortly.',
        applicationId: applicationId
      };

    } catch (error) {
      console.error('Error submitting application:', error);

      // If the error is related to the model not being available, try fallback
      if (error instanceof Error && (
        error.message.includes('Cannot read properties of undefined') ||
        error.message.includes('models.Application') ||
        error.message.includes('create')
      )) {
        // console.log('Application model not available');
      }

      return {
        success: false,
        message: `Failed to submit application: ${error instanceof Error ? error.message : 'Unknown error'}. Please check the troubleshooting guide.`
      };
    }
  }

  private async uploadFiles(formData: FormData): Promise<{
    logoAI?: string;
    logoJPEG?: string;
    brandGuideline?: string;
    entry_photo?: string;
    judging_doc_material?: string
  }> {
    const fileUrls: {
      logoAI?: string;
      logoJPEG?: string;
      brandGuideline?: string;
      entry_photo?: string;
      judging_doc_material?: string
    } = {};

    // Temporarily sign out to ensure guest access for file uploads
    try {
      await getCurrentUser();
      await signOut();
    } catch (error) {
      // User was not authenticated, continue with guest access
    }

    try {
      // Upload AI/EPS logo
      if (formData.company_logo_1) {
        // console.log('Uploading AI/EPS logo:', formData.company_logo_1.name);
        const logoKey = `applications/logos/${Date.now()}_${formData.company_logo_1.name}`;
        const result = await uploadData({
          key: logoKey,
          data: formData.company_logo_1,
          options: {
            contentType: formData.company_logo_1.type,
            accessLevel: 'guest'
          }
        }).result;
        fileUrls.logoAI = result.key;
        // console.log('AI/EPS logo uploaded successfully:', result.key);
      }

      // Upload JPEG logo
      if (formData.company_logo_2) {
        // console.log('Uploading JPEG logo:', formData.company_logo_2.name);
        const logoKey = `applications/logos/${Date.now()}_${formData.company_logo_2.name}`;
        const result = await uploadData({
          key: logoKey,
          data: formData.company_logo_2,
          options: {
            contentType: formData.company_logo_2.type,
            accessLevel: 'guest'
          }
        }).result;
        fileUrls.logoJPEG = result.key;
        // console.log('JPEG logo uploaded successfully:', result.key);
      }

      // Upload brand guidelines
      if (formData.corporate_guideline) {
        // console.log('Uploading brand guidelines:', formData.corporate_guideline.name);
        const guidelineKey = `applications/guidelines/${Date.now()}_${formData.corporate_guideline.name}`;
        const result = await uploadData({
          key: guidelineKey,
          data: formData.corporate_guideline,
          options: {
            contentType: formData.corporate_guideline.type,
            accessLevel: 'guest'
          }
        }).result;
        fileUrls.brandGuideline = result.key;
        // console.log('Brand guidelines uploaded successfully:', result.key);
      }

      // Upload entry photo
      if (formData.entry_photo) {
        // console.log('Uploading entry photo:', formData.entry_photo.name);
        const photoKey = `applications/photos/${Date.now()}_${formData.entry_photo.name}`;
        const result = await uploadData({
          key: photoKey,
          data: formData.entry_photo,
          options: {
            contentType: formData.entry_photo.type,
            accessLevel: 'guest'
          }
        }).result;
        fileUrls.entry_photo = result.key;
        // console.log('Entry photo uploaded successfully:', result.key);
      }

      // Upload judging document
      if (formData.judging_doc_material) {
        // console.log('Uploading judging document:', formData.judging_doc_material.name);
        const docKey = `applications/documents/${Date.now()}_${formData.judging_doc_material.name}`;
        const result = await uploadData({
          key: docKey,
          data: formData.judging_doc_material,
          options: {
            contentType: formData.judging_doc_material.type,
            accessLevel: 'guest'
          }
        }).result;
        fileUrls.judging_doc_material = result.key;
        // console.log('Judging document uploaded successfully:', result.key);
      }

    } catch (error) {
      console.error('Error uploading files:', error);
      throw new Error(`Failed to upload files: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return fileUrls;
  }

  private async sendConfirmationEmail(
    applicationId: string,
    companyNameEng: string,
    companyNameChi: string,
    entryTitleEng: string,
    entryTitleChi: string,
    primaryName: string,
    primaryEmail: string,
    secondaryName: string,
    secondaryEmail: string,
  ): Promise<void> {
    try {
      // console.log(`📧 Sending confirmation emails for application ${applicationId}`);
      const emailData = {
        applicationId,
        companyNameEng,
        companyNameChi,
        entryTitleEng,
        entryTitleChi,
        primaryName,
        primaryEmail,
        secondaryName,
        secondaryEmail,
      };

      await getClient().queries.emailConfirmation(
        emailData, { authMode: 'apiKey' } // Use 'userPool' for authenticated users
      );
    } catch (error) {
      console.error('❌ Error preparing confirmation emails:', error);
      // Don't throw error as email failure shouldn't fail the entire submission
    }
  }

  async getApplications(): Promise<ApplicationSubmission[]> {
    try {
      // console.log('📋 Fetching all applications...');

      // Check authentication status
      try {
        await getCurrentUser();
      } catch (authError) {
        console.error('❌ User not authenticated:', authError);
        throw new Error('User must be authenticated to access applications');
      }

      // Try to get from DynamoDB first
      if (isApplicationModelAvailable()) {
        // console.log('🔄 Attempting to fetch from DynamoDB...');
        const result = await getClient().models.Application.list({
          authMode: 'userPool'
        });
        // console.log('📊 Applications fetched from DynamoDB:', result);

        // Check if we have data, even if there are errors
        if (result.data && result.data.length > 0) {
          // console.log(`✅ Found ${result.data.length} applications in DynamoDB`);

          // Log any errors for debugging but don't fail
          if (result.errors && result.errors.length > 0) {
            console.warn(`⚠️ Found ${result.errors.length} errors in DynamoDB response, but proceeding with data`);
            // Show first few errors for debugging
            console.warn('First 3 errors:', result.errors.slice(0, 3));
          }

          try {
            // console.log('🔄 Mapping DynamoDB data to ApplicationSubmission format...');
            // console.log(`📊 Total items from DynamoDB: ${result.data.length}`);

            const validItems = result.data.filter(app => app !== null && app !== undefined);
            // console.log(`✅ Valid items after filtering: ${validItems.length}`);

            if (validItems.length < result.data.length) {
              console.warn(`⚠️ Filtered out ${result.data.length - validItems.length} null/undefined items`);
            }

            const mappedData = validItems.map(app => {
              // Parse the applicationDetail JSON string back to object
              const applicationDetail = deserializeApplicationDetail(app.applicationDetail || '{}');
              return {
                id: app.id,
                applicationId: app.applicationId,
                applicationYear: app.applicationYear,
                applicationDetail,
                applicationStatus: app.applicationStatus || 'submit success',
                status: app.status || 'ENABLED',
                createdAt: app.createdAt,
                updatedAt: app.updatedAt,
                updatedBy: app.updatedBy,
              } as ApplicationSubmission;
            });
            // console.log('✅ Successfully mapped DynamoDB data:', mappedData.length, 'applications');
            return mappedData;
          } catch (mappingError) {
            console.error('❌ Error mapping DynamoDB data:', mappingError);
          }
        } else {
          // console.log('📭 No applications found in DynamoDB');
        }
      }

      // Return empty array if no data found
      return [];
    } catch (error) {
      console.error('❌ Error fetching applications:', error);
      return [];
    }
  }

  async getApplicationById(id: string): Promise<ApplicationSubmission | null> {
    try {
      // console.log('Fetching application by ID:', id);

      // Try DynamoDB first
      if (isApplicationModelAvailable()) {
        const result = await getClient().models.Application.get({
          id
        }, {
          authMode: 'userPool' // Authenticated users only for reading applications
        });
        // console.log('Application fetched from DynamoDB:', result);

        if (result.data) {
          // Parse the applicationDetail JSON string back to object
          const applicationDetail = deserializeApplicationDetail(result.data.applicationDetail || '{}');
          return {
            id: result.data.id,
            applicationId: result.data.applicationId,
            applicationYear: result.data.applicationYear,
            applicationDetail,
            applicationStatus: result.data.applicationStatus || 'submit success',
            status: result.data.status || 'ENABLED',
            createdAt: result.data.createdAt,
            updatedAt: result.data.updatedAt,
            updatedBy: result.data.updatedBy,
          } as ApplicationSubmission;
        }
      }

      return null;
    } catch (error) {
      console.error('Error fetching application:', error);
      return null;
    }
  }

  async updateApplication(id: string, updateData: Partial<ApplicationSubmission>): Promise<{ success: boolean; message: string }> {
    try {
      // console.log('Updating application:', id, updateData);

      // Get current user for updatedBy field
      let currentUsername = 'system';
      try {
        const currentUser = await getCurrentUser();
        currentUsername = currentUser.username;
        // console.log('Current user for updatedBy:', currentUsername);
      } catch (error) {
        // console.log('Could not get current user, using "system":', error);
      }

      // Try DynamoDB first
      if (isApplicationModelAvailable()) {
        // Get the current application to merge with updates
        const currentApp = await this.getApplicationById(id);
        if (!currentApp) {
          return {
            success: false,
            message: 'Application not found'
          };
        }

        // Merge current data with updates
        const mergedData = { ...currentApp, ...updateData };

        // Serialize the applicationDetail if it's being updated
        const serializedDetail = mergedData.applicationDetail
          ? serializeApplicationDetail(mergedData.applicationDetail)
          : serializeApplicationDetail(currentApp.applicationDetail);

        const result = await getClient().models.Application.update({
          id,
          applicationDetail: serializedDetail,
          applicationStatus: mergedData.applicationStatus || currentApp.applicationStatus,
          status: mergedData.status || currentApp.status,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUsername
        }, {
          authMode: 'userPool' // Authenticated users only for updating applications
        });

        // console.log('Application updated in DynamoDB:', result);

        if (result.data) {
          return {
            success: true,
            message: 'Application updated successfully'
          };
        } else {
          return {
            success: false,
            message: 'Failed to update application'
          };
        }
      } else {
        return {
          success: false,
          message: 'Application model not available'
        };
      }
    } catch (error) {
      console.error('Error updating application:', error);
      return {
        success: false,
        message: `Error updating application: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

export const applicationService = new ApplicationService();